import { apiClient, ApiError } from '@/services/api-client'
import { useAuthStore } from '@/store/auth-store'
import { isAnalyticsEnabled } from '@/features/analytics/services/analytics-config'
import type {
  AnalyticsEventCategory,
  AnalyticsEventName,
  TrackEventOptions,
} from '@/features/analytics/types/analytics-tracker.types'

// ─── Config ─────────────────────────────────────────────────────────────────

/** Limite de eventos por lote — reflete o limite aceito por `POST /usage/events`. */
const MAX_BATCH_SIZE = 50
/** Intervalo do flush automático. */
const FLUSH_INTERVAL_MS = 10_000
/** Tentativas de envio por lote antes de descartá-lo silenciosamente. */
const MAX_RETRY_ATTEMPTS = 3
/** Backoff exponencial simples: 1s, 2s, 4s. */
const RETRY_BASE_DELAY_MS = 1000

const EVENTS_ENDPOINT = '/usage/events'

const EVENT_NAME_PATTERN = /^[a-z_]+\.[a-z_]+$/

const VALID_EVENT_CATEGORIES: readonly AnalyticsEventCategory[] = [
  'screen',
  'feature',
  'action',
  'session',
  'error',
  'performance',
]

// ─── State (singleton) ──────────────────────────────────────────────────────

interface QueuedAnalyticsEvent {
  eventName: string
  feature?: string
  screen?: string
  metadata?: Record<string, unknown>
  occurredAt: string
}

interface TrackEventsRequestBody {
  sessionId: string
  platform: 'web'
  appVersion: string
  events: QueuedAnalyticsEvent[]
}

const queue: QueuedAnalyticsEvent[] = []
let currentSessionId: string | null = null
let flushTimerId: ReturnType<typeof setInterval> | null = null

// ─── Helpers ────────────────────────────────────────────────────────────────

// 4xx (exceto timeout/rate limit) indica payload rejeitado pela API — repetir não muda o resultado.
function isNonRetryableError(error: unknown): error is ApiError {
  return (
    error instanceof ApiError &&
    error.status >= 400 &&
    error.status < 500 &&
    error.status !== 408 &&
    error.status !== 429
  )
}

// Checagem em runtime porque o tipo AnalyticsEventName não valida caracteres após o ponto.
function isValidEventName(eventName: string): boolean {
  if (!EVENT_NAME_PATTERN.test(eventName)) return false
  const category = eventName.split('.')[0]
  return (VALID_EVENT_CATEGORIES as readonly string[]).includes(category)
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function ensureFlushTimerStarted(): void {
  if (flushTimerId !== null) return
  flushTimerId = setInterval(() => {
    void flush()
  }, FLUSH_INTERVAL_MS)
}

// Reproduz a montagem de BASE_URL de api-client.ts — precisa de fetch direto aqui porque
// apiClient não suporta a opção `keepalive`, necessária para envio no unload da aba.
// Exportada para reuso em analytics-session.service.ts (mesmo cenário de keepalive).
export function getApiV1BaseUrl(): string {
  return (window.__NAVEGA_CONFIG__?.apiUrl ?? import.meta.env.VITE_API_URL ?? '') + '/api/v1'
}

// Nunca lança: falha após esgotar tentativas ou 401 (já tratado via navega:session-expired) descarta o lote.
async function sendBatchWithRetry(batch: QueuedAnalyticsEvent[], sessionId: string): Promise<void> {
  const token = useAuthStore.getState().accessToken
  if (!token) {
    console.warn('[analytics] lote descartado: nenhum token de autenticação disponível')
    return
  }

  const body: TrackEventsRequestBody = {
    sessionId,
    platform: 'web',
    appVersion: __APP_VERSION__,
    events: batch,
  }

  for (let attempt = 1; attempt <= MAX_RETRY_ATTEMPTS; attempt++) {
    try {
      await apiClient.post<unknown>(EVENTS_ENDPOINT, body, { token })
      return
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        console.warn('[analytics] lote descartado: token expirado (401)')
        return
      }

      if (isNonRetryableError(error)) {
        console.warn(
          `[analytics] lote descartado: rejeitado pela API (${String(error.status)})`,
          error,
        )
        return
      }

      const isLastAttempt = attempt === MAX_RETRY_ATTEMPTS
      if (isLastAttempt) {
        console.warn(
          `[analytics] lote descartado após ${String(MAX_RETRY_ATTEMPTS)} tentativas de envio`,
          error,
        )
        return
      }

      await delay(RETRY_BASE_DELAY_MS * 2 ** (attempt - 1))
    }
  }
}

// ─── Public API ─────────────────────────────────────────────────────────────

// metadata nunca deve conter PII (CPF, senha, token, e-mail completo) — responsabilidade de quem chama.
export function enqueueEvent(eventName: AnalyticsEventName, options: TrackEventOptions = {}): void {
  if (!isAnalyticsEnabled()) return

  if (!isValidEventName(eventName)) {
    console.warn(`[analytics] eventName inválido, evento descartado: "${eventName}"`)
    return
  }

  const category = eventName.split('.')[0]
  if (category === 'session') {
    console.warn(
      `[analytics] eventos de categoria "session" não são enviados via fila de eventos; use o lifecycle de sessão (POST /usage/sessions). Evento descartado: "${eventName}"`,
    )
    return
  }

  queue.push({
    eventName,
    feature: options.feature,
    screen: options.screen,
    metadata: options.metadata,
    occurredAt: new Date().toISOString(),
  })

  ensureFlushTimerStarted()

  if (queue.length >= MAX_BATCH_SIZE) {
    void flush()
  }
}

export async function flush(): Promise<void> {
  if (!isAnalyticsEnabled()) return

  // A API exige sessionId: eventos enfileirados antes de a sessão abrir aguardam o próximo flush.
  while (queue.length > 0 && currentSessionId) {
    const batch = queue.splice(0, MAX_BATCH_SIZE)
    await sendBatchWithRetry(batch, currentSessionId)
  }
}

export function setSessionId(sessionId: string | null): void {
  currentSessionId = sessionId
}

// Best-effort para beforeunload/visibilitychange: limpa a fila de forma síncrona e dispara um
// fetch com keepalive sem aguardar resposta — sem retry, pois não há tempo antes do unload da aba.
export function flushOnUnload(): void {
  if (!isAnalyticsEnabled()) return
  if (queue.length === 0) return

  // Limpa a fila local imediatamente, independentemente do resultado do envio abaixo.
  const pending = queue.splice(0, queue.length)

  const token = useAuthStore.getState().accessToken
  if (!token || !currentSessionId) return

  // fetch com keepalive tem limite de payload combinado por navegador — mantém só o lote mais
  // recente se a fila estourar o tamanho normal de um lote (caso raro; fila costuma ter poucos itens).
  const events = pending.length > MAX_BATCH_SIZE ? pending.slice(-MAX_BATCH_SIZE) : pending

  const body: TrackEventsRequestBody = {
    sessionId: currentSessionId,
    platform: 'web',
    appVersion: __APP_VERSION__,
    events,
  }

  fetch(`${getApiV1BaseUrl()}${EVENTS_ENDPOINT}`, {
    method: 'POST',
    keepalive: true,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  }).catch(() => {
    // Best-effort: a aba já pode estar fechando, não há como reagir a uma falha aqui.
  })
}

// Uso exclusivo em testes — isola cada it() sem depender de vi.resetModules(), que não reinstancia mocks.
export function __resetAnalyticsTrackerForTests(): void {
  queue.length = 0
  currentSessionId = null
  if (flushTimerId !== null) {
    clearInterval(flushTimerId)
    flushTimerId = null
  }
}
